// Quality Analysis API service
import { authenticatedFetch } from '@/utils/authenticated-fetch';

/**
 * Fetch IP quality analysis data from unified backend API
 * @param {string} ip - IP address to analyze
 * @returns {Promise<Object>} Quality analysis data
 */
export async function fetchQualityAnalysis(ip) {
    try {
        const url = `/api/ip-quality-analysis?ip=${encodeURIComponent(ip)}`;
        const response = await authenticatedFetch(url);
        
        if (response.error) {
            throw new Error(response.error);
        }
        
        return response;
    } catch (error) {
        console.error('Error fetching quality analysis:', error);
        throw error;
    }
}

/**
 * Fetch individual provider data
 * @param {string} provider - Provider name ('ipqualityscore', 'abuseipdb', 'scamalytics')
 * @param {string} ip - IP address to analyze
 * @returns {Promise<Object>} Provider specific data
 */
export async function fetchProviderData(provider, ip) {
    try {
        const url = `/api/${provider}?ip=${encodeURIComponent(ip)}`;
        const response = await authenticatedFetch(url);
        
        if (response.error) {
            throw new Error(response.error);
        }
        
        return response;
    } catch (error) {
        console.error(`Error fetching ${provider} data:`, error);
        throw error;
    }
}

/**
 * Get quality score color based on score value
 * @param {number} score - Quality score (0-100)
 * @returns {string} Color class or hex color
 */
export function getQualityScoreColor(score) {
    if (score >= 85) return '#28a745'; // Green - Excellent
    if (score >= 70) return '#6f42c1'; // Purple - Good  
    if (score >= 50) return '#ffc107'; // Yellow - Fair
    if (score >= 30) return '#fd7e14'; // Orange - Poor
    return '#dc3545'; // Red - Very Poor
}

/**
 * Get proxy status color based on status
 * @param {string} status - Proxy status
 * @returns {string} Color class or hex color
 */
export function getProxyStatusColor(status) {
    const statusColors = {
        'Clean IP': '#28a745', // Green
        'Low Risk IP': '#6f42c1', // Purple
        'Potentially Risky IP': '#ffc107', // Yellow
        'Suspicious IP': '#fd7e14', // Orange
        'High Risk IP': '#dc3545', // Red
        'Possible Proxy/VPN': '#ffc107', // Yellow
        'Likely Proxy/VPN': '#fd7e14', // Orange
        'High Risk Proxy/VPN': '#dc3545', // Red
    };
    
    return statusColors[status] || '#6c757d'; // Gray for unknown
}

/**
 * Get confidence level icon
 * @param {string} confidence - Confidence level
 * @returns {string} Bootstrap icon class
 */
export function getConfidenceIcon(confidence) {
    const iconMap = {
        'High': 'bi-check-circle-fill',
        'Medium': 'bi-dash-circle-fill',
        'Low': 'bi-x-circle-fill'
    };
    
    return iconMap[confidence] || 'bi-question-circle-fill';
}

/**
 * Format provider consensus data for display
 * @param {Object} consensus - Provider consensus data
 * @returns {Object} Formatted consensus display data
 */
export function formatConsensusData(consensus) {
    const formatAgreement = (level) => {
        const levelMap = {
            'High': { text: 'High Agreement', class: 'text-success', icon: 'bi-check-circle-fill' },
            'Medium': { text: 'Medium Agreement', class: 'text-warning', icon: 'bi-dash-circle-fill' },
            'Low': { text: 'Low Agreement', class: 'text-danger', icon: 'bi-x-circle-fill' }
        };
        
        return levelMap[level] || { text: 'Unknown', class: 'text-muted', icon: 'bi-question-circle-fill' };
    };
    
    return {
        type: formatAgreement(consensus.type_agreement),
        proxy: formatAgreement(consensus.proxy_agreement),
        nativeness: formatAgreement(consensus.nativeness_agreement)
    };
}
